import type { Call, Device as TwilioDevice, TwilioError } from "@twilio/voice-sdk";
import { supabase } from "@/integrations/supabase/client";
import { describeHandoffError, HandoffAttemptLog } from "./HandoffAttemptLog";

export type VoiceBridgeStatus = "idle" | "requesting-token" | "connecting" | "in-call" | "ended" | "failed";

export interface VoiceBridgeTranscriptMessage {
  message: string;
  isUser: boolean;
}

// Signalling and media negotiation each settle within a few seconds on a
// healthy connection. Past this the visitor is better served by a clear
// failure than by an endless "Connecting…".
const CALL_SETUP_STAGE_TIMEOUT_MS = 15_000;
const CALL_SETUP_TIMEOUT_MESSAGE = "The call couldn't connect. Please try again.";
const CALL_ENDED_EARLY_MESSAGE = "The call ended before it connected. Please try again.";

const toError = (error: unknown, fallback: string): Error =>
  error instanceof Error && error.message ? error : new Error(fallback);

const settleWithin = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(CALL_SETUP_TIMEOUT_MESSAGE)), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });

export class VoiceBridgeService {
  private device: TwilioDevice | null = null;
  private call: Call | null = null;
  private agentId: string;
  private destination: { id: number; label: string | null } | null = null;
  private status: VoiceBridgeStatus = "idle";
  private log: HandoffAttemptLog | null = null;
  private onStatusChangeCallback: ((status: VoiceBridgeStatus) => void) | null = null;
  private onErrorCallback: ((error: Error) => void) | null = null;
  // Once we've initiated hangup ourselves, Twilio's gateway can still emit a
  // trailing "error"/"disconnect" event as part of the normal teardown
  // handshake (e.g. ConnectionError 31005 sent from the gateway on HANGUP).
  // That's not a real failure -- suppress status/error callbacks after we've
  // already called disconnect(), so the UI doesn't flip to "failed" on a
  // call the user intentionally ended.
  private hangupInitiated: boolean = false;

  constructor(agentId: string) {
    if (!agentId) {
      throw new Error("Agent ID is required");
    }
    this.agentId = agentId;
  }

  public setOnStatusChange(callback: (status: VoiceBridgeStatus) => void): void {
    this.onStatusChangeCallback = callback;
  }

  public setOnError(callback: (error: Error) => void): void {
    this.onErrorCallback = callback;
  }

  private setStatus(status: VoiceBridgeStatus): void {
    this.status = status;
    this.onStatusChangeCallback?.(status);
  }

  public getStatus(): VoiceBridgeStatus {
    return this.status;
  }

  public getDestinationLabel(): string | null {
    return this.destination?.label ?? null;
  }

  private async mintAccessToken(transcript: VoiceBridgeTranscriptMessage[]): Promise<string> {
    const { data, error } = await supabase.functions.invoke("twilio-handler", {
      body: {
        action: "mint-voice-access-token",
        data: { agentId: this.agentId, transcript },
      },
    });

    if (error) throw new Error(error.message);
    if (!data?.token) throw new Error("No access token returned");
    this.destination = data.destination ?? null;
    return data.token;
  }

  // Runs one step of call setup and logs how it ended. A step that finishes
  // after the visitor already hung up is abandoned rather than carried on.
  private async stage<T>(
    name: string,
    run: () => Promise<T>,
    describe?: (result: T) => Record<string, unknown>,
  ): Promise<T> {
    const startedAt = Date.now();
    try {
      const result = await run();
      if (this.hangupInitiated) throw new Error("Call setup was abandoned");
      this.log?.record(name, "success", { duration_ms: Date.now() - startedAt, ...describe?.(result) });
      return result;
    } catch (error) {
      if (!this.hangupInitiated) {
        this.log?.record(name, "fail", { duration_ms: Date.now() - startedAt, ...describeHandoffError(error) });
      }
      throw error;
    }
  }

  private async recordEnvironment(): Promise<void> {
    if (!this.log) return;

    let microphonePermission = "unknown";
    try {
      const permission = await navigator.permissions.query({ name: "microphone" as PermissionName });
      microphonePermission = permission.state;
    } catch {
      // Not every browser exposes the microphone permission.
    }

    this.log.record("started", "success", {
      microphone_permission: microphonePermission,
      online: navigator.onLine,
      visibility: document.visibilityState,
      embedded: window.self !== window.top,
      page: window.location.pathname,
      user_agent: navigator.userAgent,
    });
  }

  // A device-level failure (bad token, signalling unreachable) arrives as an
  // event, not as a rejection of device.connect() — which would otherwise wait
  // forever. The returned promise turns it into a rejection during call setup.
  private watchDevice(device: TwilioDevice): Promise<never> {
    const failure = new Promise<never>((_, reject) => {
      device.on("error", (twilioError: TwilioError.TwilioError) => {
        if (this.hangupInitiated) return;
        const error = toError(twilioError, "Twilio device error");
        if (this.status !== "in-call") {
          reject(error);
          return;
        }
        this.log?.record("device_error", "fail", describeHandoffError(twilioError));
        this.setStatus("failed");
        this.onErrorCallback?.(error);
      });
    });
    failure.catch(() => {});
    return failure;
  }

  private untilAccepted(call: Call): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      call.once("accept", () => resolve());
      call.once("error", (twilioError: TwilioError.TwilioError) => reject(toError(twilioError, "Call error")));
      call.once("cancel", () => reject(new Error(CALL_ENDED_EARLY_MESSAGE)));
      call.once("disconnect", () => reject(new Error(CALL_ENDED_EARLY_MESSAGE)));
    });
  }

  private watchLiveCall(call: Call): void {
    call.on("disconnect", () => {
      if (this.hangupInitiated) return;
      this.log?.record("call_ended");
      this.setStatus("ended");
    });
    call.on("cancel", () => {
      if (this.hangupInitiated) return;
      this.log?.record("call_cancelled");
      this.setStatus("ended");
    });
    call.on("error", (twilioError: TwilioError.TwilioError) => {
      if (this.hangupInitiated) return;
      this.log?.record("call_error", "fail", describeHandoffError(twilioError));
      this.setStatus("failed");
      this.onErrorCallback?.(toError(twilioError, "Call error"));
    });
  }

  /**
   * Resolves true once Twilio has accepted the call, false when the visitor
   * hung up while it was still being set up. Rejects when the call could not
   * be established — nothing is left running in that case.
   */
  public async connect(transcript: VoiceBridgeTranscriptMessage[] = [], log?: HandoffAttemptLog): Promise<boolean> {
    this.log = log ?? null;

    try {
      void this.recordEnvironment();

      this.setStatus("requesting-token");
      const token = await this.stage(
        "token",
        () => this.mintAccessToken(transcript),
        () => ({ destination_id: this.destination?.id ?? null }),
      );

      this.setStatus("connecting");
      // Dynamically imported so @twilio/voice-sdk never loads into the bundle
      // for a normal chat session -- only when a handoff is actually triggered.
      const { Device } = await this.stage("sdk_load", () => import("@twilio/voice-sdk"));

      const device = new Device(token, { logLevel: "error" });
      this.device = device;
      const deviceFailure = this.watchDevice(device);

      const call = await this.stage("signaling", () =>
        settleWithin(
          Promise.race([
            device.connect({
              params: {
                agentId: this.agentId,
                ...(this.destination?.id != null && { destinationId: String(this.destination.id) }),
                ...(this.log && { attemptId: this.log.attemptId }),
                ...(this.log?.conversationId && { conversationId: this.log.conversationId }),
              },
            }),
            deviceFailure,
          ]),
          CALL_SETUP_STAGE_TIMEOUT_MS,
        ),
      );
      this.call = call;
      call.on("ringing", () => this.log?.record("call_ringing"));
      call.on("warning", (name: string) => this.log?.record("call_warning", "fail", { name }));

      // device.connect() resolves as soon as the call object exists — before
      // the microphone is acquired, media is negotiated, or Twilio has seen
      // the call at all. Only "accept" means there is a real call.
      await this.stage(
        "call_accept",
        () => settleWithin(Promise.race([this.untilAccepted(call), deviceFailure]), CALL_SETUP_STAGE_TIMEOUT_MS),
        () => ({ call_sid: call.parameters?.CallSid ?? null }),
      );

      this.setStatus("in-call");
      this.watchLiveCall(call);
      return true;
    } catch (error) {
      if (this.hangupInitiated) return false;

      const failure = toError(error, CALL_SETUP_TIMEOUT_MESSAGE);
      this.release();
      this.setStatus("failed");
      this.onErrorCallback?.(failure);
      throw failure;
    }
  }

  public mute(muted: boolean): void {
    this.call?.mute(muted);
  }

  public isMuted(): boolean {
    return this.call?.isMuted?.() ?? false;
  }

  private release(): void {
    this.hangupInitiated = true;
    this.call?.disconnect();
    this.device?.destroy();
    this.call = null;
    this.device = null;
  }

  public disconnect(): void {
    this.log?.record("hung_up_by_visitor", "success", { status: this.status });
    this.release();
    this.setStatus("ended");
  }

  public destroy(): void {
    this.release();
  }
}
